import { writable, get } from 'svelte/store';
import { mujocoSimulation } from '$lib/stores/mujoco';

/**
 * Store for robot controller state and commands
 */

// Control commands from IK solver (array of control values indexed by actuator ID)
export const controlCommands = writable<number[] | null>(null);

// Hand control commands from Hand IK solver (per-hand actuator arrays)
export const handControlCommands = writable<{ left: number[] | null; right: number[] | null }>({
	left: null,
	right: null
});

/**
 * Apply control commands to the MuJoCo simulator
 * Uses the control commands from the controlCommands store
 */
export function applyControlToSimulation(commands?: number[]): boolean {
	const simulation = get(mujocoSimulation);
	if (!simulation) {
		console.warn('MuJoCo simulation not available');
		return false;
	}

	// Use provided commands or get from store
	const controlCmds = commands || get(controlCommands);
	if (!controlCmds || controlCmds.length === 0) {
		console.warn('No control commands available to apply');
		return false;
	}

	try {
		// Apply control commands to MuJoCo simulation
		// The simulation.ctrl array maps actuator indices to control values
		for (let i = 0; i < controlCmds.length; i++) {
			if (simulation.ctrl && i < simulation.ctrl.length) {
				simulation.ctrl[i] = controlCmds[i];
			}
		}

		return true;
	} catch (error) {
		console.error('Failed to apply control commands to MuJoCo simulation:', error);
		return false;
	}
}

/**
 * Apply hand control commands to the MuJoCo simulator
 * Uses the hand control commands from the handControlCommands store
 *
 * @param side - Which hand to apply commands for ('left', 'right', or 'both')
 * @param startActuatorIndex - The starting actuator index for hand controls in MuJoCo
 */
export function applyHandControlToSimulation(
	side: 'left' | 'right' | 'both' = 'both',
	startActuatorIndex: { left: number; right: number } = { left: 0, right: 10 }
): boolean {
	const simulation = get(mujocoSimulation);
	if (!simulation) {
		console.warn('MuJoCo simulation not available');
		return false;
	}

	const handCmds = get(handControlCommands);
	let success = true;

	try {
		if ((side === 'left' || side === 'both') && handCmds.left) {
			for (let i = 0; i < handCmds.left.length; i++) {
				const actuatorIdx = startActuatorIndex.left + i;
				if (simulation.ctrl && actuatorIdx < simulation.ctrl.length) {
					simulation.ctrl[actuatorIdx] = handCmds.left[i];
				}
			}
		}

		if ((side === 'right' || side === 'both') && handCmds.right) {
			for (let i = 0; i < handCmds.right.length; i++) {
				const actuatorIdx = startActuatorIndex.right + i;
				if (simulation.ctrl && actuatorIdx < simulation.ctrl.length) {
					simulation.ctrl[actuatorIdx] = handCmds.right[i];
				}
			}
		}

		return success;
	} catch (error) {
		console.error('Failed to apply hand control commands to MuJoCo simulation:', error);
		return false;
	}
}

/**
 * Helper function to clear controller stores
 */
export function clearControllerStores(): void {
	controlCommands.set(null);
	handControlCommands.set({ left: null, right: null });
}
